import type React from 'react';

import type { AnalyticsEventPayload } from '@atlaskit/analytics-next';
import { fg } from '@atlaskit/platform-feature-flags';
import type { LuminaSubAgentResponseType } from '@atlassian/lumina';
import { setSessionItem } from '@atlassian/search-dialog';
import type { SearchExperimentLayer } from '@atlassian/search-experiment';

import { SAINFeatureControl } from '../../common/utils/sain-feature-control';
import { SEARCH_AI_ANSWER_CACHE_KEY } from '../../constants';
import type { IntentClassificationData } from '../../query-intent-api/types';
import { StreamAnswerGenerationTypes } from '../../stream-api/types';
import { type AIAnswerContextType } from '../ai-answer-context';
import { DEFAULT_EMPTY_NLP_SEARCH_STATE } from '../ai-answer-dialog';
import {
	getNonStreamFetchErrorEventPayload,
	getStreamFetchErrorEventPayload,
} from '../error-analytics';
import {
	type AIAnswerQueryFunctionType,
	type AIAnswerQueryStreamMarkdownFailedBackend,
	type AIAnswerQueryStreamResponse,
	AIAnswerQueryStreamType,
	type NLPDynamicLayoutSubAgentResponse,
	NLPSearchErrorCodes,
	NLPSearchErrorState,
	NLPSearchResultFormat,
	type NLPSearchType,
	type parsedFiltersType,
} from '../types';

import { checkSkipAIAnswerCall } from './smartAnswerRouteStrategy';

export type FetchAIAnswerArgs = {
	cloudId: string;
	additional_context?: string;
	locale: string;
	query: string;
	source: string;
	followUpsEnabled?: boolean;
	getAIAnswer: AIAnswerQueryFunctionType | null;
	setLoading: React.Dispatch<React.SetStateAction<boolean>>;
	partialResponse: AIAnswerContextType['partialResponse'];
	partialResponseAbortControllerRef: React.MutableRefObject<AbortController | undefined>;
	setPartialResponse: React.Dispatch<React.SetStateAction<AIAnswerContextType['partialResponse']>>;
	setData: React.Dispatch<React.SetStateAction<NLPSearchType>>;
	setAnswerStreamed: React.Dispatch<React.SetStateAction<boolean>>;
	parsedFilters?: parsedFiltersType;
	selectedProduct?: string;
	isReadingAids: boolean;
	fireAnalyticsEvent: (payload: AnalyticsEventPayload) => void;
	setQueryReasoning: React.Dispatch<
		React.SetStateAction<AIAnswerContextType['queryReasoning']> | undefined
	>;
	setDynamicLayoutSubAgentMessages: React.Dispatch<
		React.SetStateAction<NLPDynamicLayoutSubAgentResponse[] | LuminaSubAgentResponseType[]>
	>;
	setIsLuminaAnswer: React.Dispatch<boolean>;
	experimentContext?: {
		experimentLayers: SearchExperimentLayer[];
	};
	queryIntentClassificationData?: IntentClassificationData;
	referrer?: string | null;
};

export const fetchAIAnswer = async ({
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	cloudId,
	locale,
	query,
	additional_context,
	source,
	followUpsEnabled = false,
	getAIAnswer,
	setLoading,
	partialResponse,
	partialResponseAbortControllerRef,
	setPartialResponse,
	setData,
	setAnswerStreamed,
	parsedFilters,
	selectedProduct,
	isReadingAids,
	fireAnalyticsEvent,
	setQueryReasoning,
	setDynamicLayoutSubAgentMessages,
	setIsLuminaAnswer,
	experimentContext,
	queryIntentClassificationData,
	referrer,
}: FetchAIAnswerArgs): Promise<void> => {
	if (!getAIAnswer) {
		return;
	}
	if (partialResponse?.partialNlpResult) {
		// clean up any old partial responses
		setPartialResponse(undefined);
	}
	// end any old partial response streams
	partialResponseAbortControllerRef.current?.abort();
	const abortController = new AbortController();
	partialResponseAbortControllerRef.current = abortController;

	// Check if we can skip the AI answer call based on intent classification data.
	// If the intent already has the data we need (e.g., PersonIntent with persons[]),
	// use it directly instead of making another API call.
	if (fg('kd_fe_persons_intent_detection_els')) {
		const skipResult = checkSkipAIAnswerCall(queryIntentClassificationData);
		if (skipResult.skip) {
			setData(skipResult.data);
			setSessionItem(SEARCH_AI_ANSWER_CACHE_KEY, JSON.stringify({ query, data: skipResult.data }));
			setAnswerStreamed(true);
			setLoading(false);
			return;
		}
	}

	// For graphQL getAIAnswer fns we expect each product to pass their own cloudIdARI in order for this component to be more product agnostic
	const aiAnswer = getAIAnswer({
		variables: {
			query,
			additional_context,
			locale,
			filters: parsedFilters,
			selectedProduct,
			experience: isReadingAids ? 'readingAids' : source,
			followUpsEnabled,
			experimentContext,
			isQuerySuggestion: !!referrer && referrer === 'zeroQuery',
		},
	});

	const processStreamResponse = async (answer: AIAnswerQueryStreamResponse) => {
		try {
			let next = await answer.stream.next();
			let answerFormat;

			while (!next.done) {
				// setLoading needed here for instances where stream gets interrupted and restarts, ensuring loading state is set while stream is active
				setLoading(true);
				const streamEvent = next.value;

				if (streamEvent.type === AIAnswerQueryStreamType.AnswerType) {
					answerFormat = streamEvent.message.format;
				}
				if (
					streamEvent.type === AIAnswerQueryStreamType.GenerationType &&
					SAINFeatureControl.isLuminaEnabled()
				) {
					if (streamEvent.message.answer_generation_type === StreamAnswerGenerationTypes.LUMINA) {
						setIsLuminaAnswer(true);
					}
				}
				if (streamEvent.type === AIAnswerQueryStreamType.Trace) {
					if (answerFormat !== NLPSearchResultFormat.MARKDOWN) {
						throw new Error(`Unexpected format: ${answerFormat}`);
					}
					setQueryReasoning(streamEvent.message);
				}
				if (streamEvent.type === AIAnswerQueryStreamType.AnswerPart) {
					if (answerFormat !== NLPSearchResultFormat.MARKDOWN) {
						throw new Error(`Unexpected format: ${answerFormat}`);
					}

					setPartialResponse((previousPartialResponse) => {
						let previousPartialNlpResult: string = previousPartialResponse?.partialNlpResult || '';
						return {
							format: NLPSearchResultFormat.MARKDOWN,
							partialNlpResult: previousPartialNlpResult + streamEvent.message.nlpResult,
						};
					});
				}

				if (SAINFeatureControl.isLuminaEnabled()) {
					if (streamEvent.type === AIAnswerQueryStreamType.DynamicLayoutSubAgentMessage) {
						setDynamicLayoutSubAgentMessages((prevMessages) => [
							...prevMessages,
							streamEvent.message,
						]);
					}
				}

				if (streamEvent.type === AIAnswerQueryStreamType.FinalResponse) {
					setPartialResponse(undefined);
					setData(streamEvent.message);
					setLoading(false);
				}

				next = await answer.stream.next();
				if (abortController.signal.aborted) {
					// eslint-disable-next-line no-throw-literal
					throw 'Aborted';
				}
			}

			const streamEnd = next.value;
			if (abortController.signal.aborted) {
				// eslint-disable-next-line no-throw-literal
				throw streamEnd;
			}

			if (streamEnd.state === 'complete') {
				setPartialResponse(undefined);
				setData(streamEnd.value.message);
				// Only store to cache if Lumina is disabled
				if (isReadingAids || !SAINFeatureControl.isLuminaEnabled()) {
					setSessionItem(
						SEARCH_AI_ANSWER_CACHE_KEY,
						JSON.stringify({
							query: query,
							data: streamEnd.value.message,
						}),
					);
				}
			} else {
				if (streamEnd.reason === 'timeout') {
					setPartialResponse(undefined);
					setData({
						errorState: NLPSearchErrorState.TimeoutError,
						errorCode: 0,
						format: null,
						disclaimer: null,
						nlpResults: [],
						uniqueSources: [],
						nlpFollowUpResults: null,
					});
				} else {
					throw streamEnd;
				}
			}
		} catch (error: unknown) {
			fireAnalyticsEvent(getStreamFetchErrorEventPayload(error, source));
			if (error === 'Aborted') {
				return;
			}
			setPartialResponse(undefined);
			setData({
				errorState:
					typeof error === 'object' &&
					error !== null &&
					'details' in error &&
					error?.details === NLPSearchErrorCodes.HIPAA_ENABLED_ERROR
						? NLPSearchErrorState.HIPAAError
						: NLPSearchErrorState.NetworkError,
				errorCode: (error as AIAnswerQueryStreamMarkdownFailedBackend)?.errorCode
					? (error as AIAnswerQueryStreamMarkdownFailedBackend).errorCode
					: 0,
				format: null,
				disclaimer: null,
				nlpResults: [],
				uniqueSources: [],
				nlpFollowUpResults: null,
				nlpSuggestedQueries: [],
			});
		} finally {
			setAnswerStreamed(true);
			setLoading(false);
		}
	};

	// If aiAnswer is an object with a type property -- it's a getAIAnswer
	// and we need to handle the new streaming response
	if ('type' in aiAnswer) {
		processStreamResponse(aiAnswer);
		return;
	}

	await aiAnswer
		.then((data) => {
			if ('type' in data) {
				processStreamResponse(data);
				return;
			}

			setData(data?.nlpSearch || DEFAULT_EMPTY_NLP_SEARCH_STATE);
			// Only store to cache if Lumina is disabled
			if (isReadingAids || !SAINFeatureControl.isLuminaEnabled()) {
				setSessionItem(
					SEARCH_AI_ANSWER_CACHE_KEY,
					JSON.stringify({
						query: query,
						data: data?.nlpSearch || DEFAULT_EMPTY_NLP_SEARCH_STATE,
					}),
				);
			}
			setLoading(false);
		})
		.catch((error: unknown) => {
			fireAnalyticsEvent(getNonStreamFetchErrorEventPayload(error, source));
			setData({
				errorState:
					error instanceof DOMException && error.name === 'TimeoutError'
						? NLPSearchErrorState.TimeoutError
						: NLPSearchErrorState.NetworkError,
				format: null,
				disclaimer: null,
				nlpResults: [],
				uniqueSources: [],
				nlpFollowUpResults: null,
			});
			setLoading(false);
		});
};
